"use client";

import { isRTL } from "@core/shared/utils";
import { buttonVariants } from "@design-system/ui/button";
import { cn } from "@design-system/utils";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from "lucide-react";
import type { PropsBase, PropsSingle } from "react-day-picker";
import { DayPicker } from "react-day-picker";

type CalendarProps = PropsBase & PropsSingle;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  mode = "single",
  ...props
}: CalendarProps) {
  // Get the month from the selected date
  const defaultMonth =
    props.selected instanceof Date ? props.selected : undefined;

  const isRTLValue = isRTL();

  return (
    <DayPicker
      mode={mode}
      showOutsideDays={showOutsideDays}
      defaultMonth={defaultMonth}
      className={cn("p-3", className)}
      classNames={{
        root: "bg-white dark:bg-gray-800 shadow-md rounded-lg relative z-50 w-full h-full transition-colors duration-300",
        months: "flex flex-col sm:flex-row sm:gap-4",
        month: "flex flex-col gap-4",
        caption_label: "text-sm font-medium text-center block mt-2 text-gray-900 dark:text-gray-100",
        nav: "absolute flex w-full justify-between left-0 h-7",
        weekdays: "flex flex-row justify-between",
        nav_button_previous: "ms-12",
        nav_button_next: "me-12",
        button_previous: "ms-12 border",
        button_next: "me-12 border",
        table: "w-full border-collapse mb-1",
        head_row: "grid grid-cols-7",
        head_cell: cn(
          "text-muted-foreground font-normal text-[0.8rem]",
          "text-center",
        ),
        row: "grid grid-cols-7 mt-2",
        cell: "text-center text-sm p-0 relative focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md",
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal",
          "hover:bg-accent hover:text-accent-foreground",
          "focus:bg-accent focus:text-accent-foreground",
        ),
        selected:
          "bg-primary text-primary-foreground font-medium hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside: "text-muted-foreground opacity-50",
        day_button: "w-full h-full",
        day_disabled: "text-muted-foreground opacity-50 cursor-not-allowed",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          const iconClass = "text-muted-foreground";

          switch (orientation) {
            case "left":
              return isRTLValue ? (
                <ChevronRightIcon className={iconClass} />
              ) : (
                <ChevronLeftIcon className={iconClass} />
              );
            case "right":
              return isRTLValue ? (
                <ChevronLeftIcon className={iconClass} />
              ) : (
                <ChevronRightIcon className={iconClass} />
              );
            case "up":
              return <ChevronUpIcon className={iconClass} />;
            case "down":
              return <ChevronDownIcon className={iconClass} />;
            default:
              return <div />;
          }
        },
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
